/* eslint-disable indent */
import React, { useState } from 'react';
import {
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Select,
    MenuItem,
    Table,
    TableRow,
    TableCell,
    IconButton,
    TableHead,
    Stack,
    Chip,
    TableBody,
    TableContainer,
    Paper,
    Pagination,
    SelectChangeEvent,
    TableSortLabel,
    PaperProps,
    Skeleton,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';  
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import SpringDataPage, { BasicPage, defaultPage } from '../../../../@types/spring-data-page';
import SafetyService from '../../../../services/safety.service';
import SafetyModel from '../../../../@types/safety-model';
import { getGroupNameWithAscendants, role } from '../../../../utils/group-utils';
import Draggable from 'react-draggable';
import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import EventSequenceSummary from '../../../../@types/event-sequence-summary';
import EventSequenceService from '../../../../services/event-sequence.service';
import EventSequence from '../../../../@types/event-sequence';
import { toast } from 'react-toastify';
import { useWorkspaceContext } from '../../../../context/workspaceContext';
import { CanvasActionType } from '../../../../@types/canvas-action-type';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';


interface SelectComponentProps {
    open: boolean;
    onClose: () => void;
    env: CanvasEnvironment | null;
}

const pageSizes: number[] = [10, 25, 50];

type Order = 'asc' | 'desc';

function PaperComponent(props: PaperProps): React.ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}


function Row(props: { row: SafetyModel, selectedEventSequence: EventSequence | null, onClickItem: (event: any) => void}) : React.ReactElement {
    const { row, selectedEventSequence, onClickItem } = props;
    const [open, setOpen] = React.useState(false);
    const [redraw, setRedraw] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const groupPath = getGroupNameWithAscendants(row.group);
    const groupRole = role(row.group);

    const handleSingleClick = (event: any): void => {
        onClickItem(event);
    }

    React.useEffect(() => {
        if (open) {
            setLoading(true);
            setRedraw(false);
            SafetyService.getEventSequences(row.id).then((eventSequences: EventSequenceSummary[]) => {
                row.children = eventSequences.filter((eventSequence) => eventSequence.isComponent);
            }).finally(() => {
                setLoading(false);
                setRedraw(true);
            });
        }
    }, [open]);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={(): void => setOpen(!open)}
                    >
                        {open ? (loading ?
                            <FontAwesomeIcon icon={ faSpinner } spin/> : <KeyboardArrowDownIcon />) : <KeyboardArrowRightIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {row.name}
                </TableCell>
                <TableCell align="right">
                    <Stack direction='row' spacing={1} sx={{ justifyContent: 'flex-end' }}>
                        <Typography variant="caption">{groupPath}</Typography>
                        <Typography variant="caption">
                            <Chip
                                label={groupRole.toUpperCase()}
                                variant="outlined" size="small" 
                                sx={{ color: '#606060', fontSize: '8px' }}
                            />
                        </Typography>
                    </Stack>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open && redraw} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Table size="small" aria-label="components">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                Unique Id
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                Name
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                Description
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                Version
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                Author
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ cursor: 'pointer' }}>
                                    {redraw && row.children?.map((eventSequence) => (
                                        <TableRow
                                            key={eventSequence.id}
                                            onClick={handleSingleClick}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    id={'eventSequenceId-' + eventSequence.id}
                                                    checked={selectedEventSequence?.id === eventSequence.id}
                                                    onChange={(): void => { }}
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell component="th" scope="row" >
                                                <Typography variant="body2" color="textSecondary" id={'eventSequenceId-' + eventSequence.id}>
                                                    {eventSequence.uniqueId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textSecondary" id={'eventSequenceId-' + eventSequence.id + '-1'}>
                                                    {eventSequence.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textSecondary" id={'eventSequenceId-' + eventSequence.id + '-2'}>
                                                    {eventSequence.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textSecondary" id={'eventSequenceId-' + eventSequence.id + '-3'}>
                                                    {'1.0.0'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="textSecondary" id={'eventSequenceId-' + eventSequence.id + '-5'}>
                                                    {'ian@isa-software.com'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}



const SelectComponentModal: React.FC<SelectComponentProps> = ({
    open,
    onClose,
    env
}) => {
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<string>('group');
    const [page, setPage] = useState<BasicPage>(defaultPage);
    const [safetyModels, setSafetyModels] = useState<SpringDataPage<SafetyModel> | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingEventSequence, setLoadingEventSequence] = useState<boolean>(false);

    const [selectedEventSequence, setSelectedEventSequence] = useState<EventSequence | null>(null);
    const { setCurrentCanvasAction } = useWorkspaceContext();
    
    const onEventSequenceSelected = (event: any): void => {
        let eventSequenceId = event.target.id.split('-')[1];

        if (!eventSequenceId) {
            return;
        }

        if (selectedEventSequence && selectedEventSequence.id == eventSequenceId) {
            setSelectedEventSequence(null);
            return;
        }
        
        setLoadingEventSequence(true);
        EventSequenceService.getEventSequenceById(eventSequenceId).then((response) => {
            setSelectedEventSequence(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setLoadingEventSequence(false);
        });
    }

    const handleConfirm = (): void => {
        if (selectedEventSequence) {
            const copiedNodeId = selectedEventSequence.events[0].faultTree?.faultTreeNodes[0].id;
            localStorage.copyContents = JSON.stringify({
                type: 'fault_tree_node',
                id: copiedNodeId
            });
            setCurrentCanvasAction({ actionType: CanvasActionType.FAULTTREENODE_PASTE_COMPONENT });
        }
        onClose();
    };

    const handleRequestSort = (
        property: string,
    ): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };    

    const onPageChange = (event: React.ChangeEvent<unknown>, pageNumber: number): void => {
        let newPage = {...page, offset: pageNumber - 1};
        setPage(newPage);
        setLoading(true);
        setSelectedEventSequence(null);
        setSafetyModels(null);
        SafetyService.fetchPagedSafetyModelsWithComponents(newPage).then((response: SpringDataPage<SafetyModel>) => {
            setSafetyModels(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setLoading(false);
        });
    }

    function onPageSizeChange(event: SelectChangeEvent<number>): void {
        let newPage: BasicPage = {...page, size: event.target.value as number, offset: 0};
        setPage(newPage);
        setLoading(true);
        setSelectedEventSequence(null);
        setSafetyModels(null);
        SafetyService.fetchPagedSafetyModelsWithComponents(newPage).then((response: SpringDataPage<SafetyModel>) => {
            setSafetyModels(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        }).finally(() => {
            setLoading(false);
        });
    }

    React.useEffect(() => {
        if (open && env) {
            const [selectedNode] = env!.selection
            if (selectedNode) {
                const lgpage = { ...defaultPage, sort: orderBy, direction: order };
                setPage(lgpage);
                setLoading(true);
                setSelectedEventSequence(null);
                setSafetyModels(null);
                SafetyService.fetchPagedSafetyModelsWithComponents(lgpage).then((response: SpringDataPage<SafetyModel>) => {
                    setSafetyModels(response);
                }).catch((error: Error) => {
                    toast.error(error.message);
                }).finally(() => {
                    setLoading(false);
                });
            }
        }

    }, [order, orderBy, open, env]);


    return (

        <Dialog open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            maxWidth='md'
            fullWidth>
            <DialogTitle>Select Component</DialogTitle>
            <DialogContent>
                {loading && <Stack direction={'column'} spacing={1} sx={{ height: '440px', marginTop: '20px', justifyContent: 'center' }} >
                    <Stack direction={'row'} spacing={1} sx={{ justifyContent: 'center' }}>
                        <Skeleton variant="rectangular" width={'50%'} height={40} />
                        <Skeleton variant="rectangular" width={'50%'} height={40} />
                    </Stack>
                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                    <Skeleton variant="rectangular" width={'100%'} height={40} />
                </Stack>}
                <Backdrop
                    sx={ (theme) : any => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1})} 
                    open={loadingEventSequence}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
                
                {!loading && <TableContainer component={Paper} sx={{ height: '440px', overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell align="left">
                                    <TableSortLabel
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('name')}
                                    >
                                        Safety Model
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="left">
                                    <TableSortLabel
                                        active={orderBy === 'group'}
                                        direction={orderBy === 'group' ? order : 'asc'}
                                        onClick={(): void => handleRequestSort('group')}
                                    >
                                        Group
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {safetyModels?.content.map((row) => (
                                <Row key={row.id} row={row} selectedEventSequence={selectedEventSequence}  onClickItem={onEventSequenceSelected} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>}
                {/** selectedEventSequence &&
                    <div style={{ marginTop: '20px'}}>
                        <Typography variant="h6" gutterBottom component="div">
                            Selected Component: 
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom component="div">
                            {`${selectedEventSequence.uniqueId} - ${selectedEventSequence.name}`}
                        </Typography>
                    </div>
                */}
                {safetyModels && (
                    <Stack direction={'row'} spacing={1} sx={{ marginTop: '20px', justifyContent: 'flex-end' }}>
                        <Select
                            variant='standard'
                            role='presentation'
                            value={page.size}
                            onChange={onPageSizeChange}
                            sx={{ fontSize: '14px' }}
                        >
                            {pageSizes.map(size => (
                                <MenuItem key={size} value={size}>{size} per page</MenuItem> ))}
                        </Select>
                        {safetyModels && safetyModels.page &&
                            <Pagination
                                count={safetyModels.page.totalPages}
                                size={'small'}
                                style={{ paddingTop: '2px', fontSize: '13px'}}
                                showFirstButton showLastButton
                                page={page?.offset ? page.offset + 1 : 1}
                                siblingCount={0}
                                boundaryCount={1}
                                onChange={onPageChange}
                            />
                        }
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    color="primary"
                    disabled={!selectedEventSequence}
                >
                Add Component
                </Button>
            </DialogActions>
            </Dialog>
    );
};

export default SelectComponentModal;